/**
 * `<JudgeScoresStrip>` (issue #217) — renders the five AI-judge results
 * for a `ContentVersion` as a row of colour-coded MUI chips. Surfaced on
 * every version slot of `ContentItemDetailPage` (DRAFT, PUBLISHED,
 * ARCHIVED) so the operator can see at a glance whether the AI output
 * passed the quality bars before publishing.
 *
 * Rendering rules:
 *   - `judges === null`     → renders nothing. The "no judge run has
 *                              landed yet" sentinel (manual / pre-AI
 *                              versions).
 *   - `judges.length === 0` → renders nothing. Defensive — the runner
 *                              always writes five rows on success — but
 *                              we still hide the strip rather than
 *                              showing an empty container.
 *   - otherwise             → renders five chips in canonical order
 *                              (ACCURACY → READABILITY → AI_SLOP → SEO →
 *                              BRAND_TONE). Missing judge types fall
 *                              back to a grey "?" chip so a partial
 *                              result set doesn't crash the strip.
 *
 * Status → MUI colour:
 *   PASS    → success (green)
 *   WARNING → warning (amber)
 *   FAIL    → error   (red)
 *   missing → default (grey, with score "?")
 *
 * Tooltip:
 *   Each chip is wrapped in `<Tooltip title={summary}>` so hovering
 *   surfaces the judge's rationale. `<Tooltip>` requires a non-disabled
 *   focusable child; `Chip` already qualifies.
 *
 * Mobile responsive:
 *   The chips live inside a `<Stack direction="row" flexWrap="wrap"
 *   useFlexGap>` so they wrap to a second line on narrow viewports
 *   (<600px). Standards file requires that.
 */

import type { ReactElement } from "react";
import { Chip, Stack, Tooltip } from "@mui/material";
import type { AiJudgeResultResponse } from "@ottopilot/shared";

type JudgeType = AiJudgeResultResponse["judgeType"];
type JudgeStatus = AiJudgeResultResponse["status"];

/**
 * Canonical render order — matches `JUDGE_TYPES` in
 * `api/src/ai/judges/types.ts` so the chip strip mirrors the order the
 * runner uses. Stable order also keeps screenshot tests deterministic.
 */
const CANONICAL_ORDER: ReadonlyArray<JudgeType> = [
  "ACCURACY",
  "READABILITY",
  "AI_SLOP",
  "SEO",
  "BRAND_TONE",
];

/**
 * Short human labels for each judge type. Kept terse so the strip fits
 * on a single line at desktop widths and wraps cleanly on mobile.
 */
const JUDGE_LABELS: Record<JudgeType, string> = {
  ACCURACY: "Accuracy",
  READABILITY: "Readability",
  AI_SLOP: "AI slop",
  SEO: "SEO",
  BRAND_TONE: "Brand tone",
};

function statusToChipColor(status: JudgeStatus): "success" | "warning" | "error" {
  switch (status) {
    case "PASS":
      return "success";
    case "WARNING":
      return "warning";
    case "FAIL":
      return "error";
  }
}

function testIdFor(judgeType: JudgeType): string {
  return `judge-chip-${judgeType.toLowerCase()}`;
}

export type JudgeScoresStripProps = {
  judges: AiJudgeResultResponse[] | null;
};

export function JudgeScoresStrip({ judges }: JudgeScoresStripProps): ReactElement | null {
  // Treat undefined the same as null — older fixtures and pre-#217 server
  // payloads may omit the field entirely, so don't let a missing key
  // crash the strip.
  if (judges == null || judges.length === 0) return null;

  // Map by judge type for O(1) lookup so we can render in canonical order
  // even when the server returns the rows in a different order (defensive
  // — today the runner always writes them in canonical order). If a type
  // appears more than once (shouldn't happen — `(contentVersionId,
  // judgeType)` is logically unique per accept run) the last write wins,
  // which mirrors the runner's idempotent `deleteMany → createManyAndReturn`
  // semantics.
  const byType = new Map<JudgeType, AiJudgeResultResponse>();
  for (const judge of judges) byType.set(judge.judgeType, judge);

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap data-testid="judge-scores-strip">
      {CANONICAL_ORDER.map((judgeType) => {
        const judge = byType.get(judgeType);
        const label = JUDGE_LABELS[judgeType];
        if (!judge) {
          // Defensive fallback — the runner always emits all five, but a
          // partial result set shouldn't crash the strip. Grey chip with
          // a `?` score makes the absence visible without alarming the
          // operator with a red FAIL signal.
          return (
            <Tooltip key={judgeType} title="No judge result available">
              <Chip
                size="small"
                color="default"
                variant="outlined"
                label={`${label} ?`}
                data-testid={testIdFor(judgeType)}
              />
            </Tooltip>
          );
        }
        return (
          <Tooltip key={judgeType} title={judge.summary}>
            <Chip
              size="small"
              color={statusToChipColor(judge.status)}
              label={`${label} ${String(judge.score)}`}
              data-testid={testIdFor(judgeType)}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}
